var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { emitCrossWindowWithData, onCrossWindow } from '../cross-window-events';
import { getJson } from '../server';
import createSlidingKeyValueCache from '../sliding-key-value-cache';
import URLX from '../Url';
import { createOpenPromise } from './open-promise';
export { useDynamicData } from './use-dynamic-data';
export const batchWaitMS = 32;
const cache = createSlidingKeyValueCache('dynamicdatacache', 50, 1000, (item, key) => (item.$cacheKey === key ? item : null), item => true, item => true, item => true, item => item.$cacheKey, 
// Only allow dropping something from the fast cache if we don't have any
// data watchers on it
key => !dataWatchers[key] || dataWatchers[key].length === 0);
// This promise and exported awaitDataResolveUntilReady() lets the project decide when they are ready to resolve data.
// The use case is in the app rendering the site on secondary tabs, which should get their dynamic data
// from the primary tab and not the network.
let awaitDataResolve;
export function awaitDataResolveUntilReady() {
    awaitDataResolve = createOpenPromise();
}
// If awaitDataResolveUntilReady() was called then this needs to be called from the project
// before the app is rendered, otherwise dynamic data will never be resolved
export function setReadyToResolveData() {
    if (awaitDataResolve) {
        awaitDataResolve.resolve();
    }
    else {
        throw 'awaitDataResolveUntilReady() was never called. This needs to be called before if you want to await data resolve.';
    }
}
const languageInit = createOpenPromise();
let requestedPropertiesBeforeLanguageInit = [];
export function setLanguage(language) {
    return __awaiter(this, void 0, void 0, function* () {
        languageInit.resolve(language);
        // Since onDataUpdate awaits languageInit we need to await it here as well
        // in order to have the data watchers registered correctly
        yield languageInit;
        requestedPropertiesBeforeLanguageInit.forEach(property => callDataWatchers(getCacheKey(property, language)));
        requestedPropertiesBeforeLanguageInit = [];
    });
}
// https://stackoverflow.com/questions/51040703/what-return-type-should-be-used-for-settimeout-in-typescript
let timer = -1;
const dataWatchers = {};
let toResolve = {};
const requestsInFlight = {};
export function nukeCache() {
    return __awaiter(this, void 0, void 0, function* () {
        yield cache.nukeCache();
        Object.keys(dataWatchers).forEach(cacheKey => {
            if (dataWatchers[cacheKey]) {
                dataWatchers[cacheKey].forEach(l => l());
            }
        });
    });
}
export function getFastCache() {
    return cache.getFastCache();
}
export function resolveDataSync(property) {
    if (!languageInit.resolvedValue) {
        requestedPropertiesBeforeLanguageInit.push(property);
        return null;
    }
    const cacheKey = getCacheKey(property, languageInit.resolvedValue);
    const fastCacheItem = cache.getFastCacheItem(cacheKey);
    if (fastCacheItem) {
        return fastCacheItem;
    }
    if (property.preloaded) {
        return property.preloaded;
    }
    return null;
}
export function createDataContext(property, context) {
    return Object.assign(Object.assign({}, property), { context: Object.assign(Object.assign({}, property.context), context) });
}
export function updateData(property, data, preloadBehavior, skipCacheBroadcast = false) {
    return __awaiter(this, void 0, void 0, function* () {
        if (preloadBehavior === 'preserveCache' &&
            (
            // First check fastCache (last 10 items)
            cache.getFastCacheItem(getCacheKey(property, yield languageInit)) ||
                // If fast cache was empty, check full cache to ensure that preloadBehavior.preserveCache is consistent.
                cache.getCacheItem(getCacheKey(property, yield languageInit)))) {
            // We've fetched a new preloaded value, but we already have an existing value in fast cache. Ignoring update.
            // This might be if you refreshed appShellData where you have a preloaded DynamicData in the appShellData.
            // If you are fetching an entire model to get the update for a DynamicData value, you're probably going at it wrong.
            // You should use the DynamicData methods and send the (maybe stale) DynamicData value from appShellData to get the latest value updated
            return;
        }
        yield addToCache(getCacheKey(property, yield languageInit), data, skipCacheBroadcast);
    });
}
export function invalidateAndResolveData(property, callWatchersOnInvalidate = true) {
    return __awaiter(this, void 0, void 0, function* () {
        yield invalidateData(property, callWatchersOnInvalidate);
        return resolveData(property);
    });
}
export function invalidateData(property, willCallWatchers = true) {
    return __awaiter(this, void 0, void 0, function* () {
        const cacheKey = getCacheKey(property, yield languageInit);
        if (cacheKey in requestsInFlight) {
            delete requestsInFlight[cacheKey];
        }
        yield cache.clearCacheItem(cacheKey);
        if (willCallWatchers) {
            callDataWatchers(cacheKey);
        }
    });
}
export function resolveData(property, refreshIfCached = true) {
    return __awaiter(this, void 0, void 0, function* () {
        const cacheKey = getCacheKey(property, yield languageInit);
        if (awaitDataResolve) {
            yield awaitDataResolve;
        }
        if (cacheKey in requestsInFlight) {
            yield requestsInFlight[cacheKey];
            return cache.getFastCacheItem(cacheKey);
        }
        const wasInFastCache = cache.getFastCacheItem(cacheKey) !== null;
        let networkResolveRequest = null;
        // Refresh the cache, but only if the item doesn't exist in the
        // fast cache. If it's in the fast cache it means that we've either
        // placed a loaded response in the cache or previously loaded it over
        // the network and placed it in the fast cache.
        if (refreshIfCached && !wasInFastCache) {
            networkResolveRequest = resolveOverNetwork(property);
        }
        return cache.getCacheItem(cacheKey).then(cached => {
            if (cached) {
                // If it wasn't in the fast cache it means that resolveDataSync() wouldn't have
                // returned it so we need to call the data watcher to let them know that we now
                // have a value
                if (!wasInFastCache) {
                    callDataWatchers(cacheKey);
                }
                return cached;
            }
            else {
                return networkResolveRequest || resolveOverNetwork(property);
            }
        });
    });
}
export function forceResolveFreshData(property) {
    return __awaiter(this, void 0, void 0, function* () {
        const cacheKey = getCacheKey(property, yield languageInit);
        if (cacheKey in requestsInFlight) {
            yield requestsInFlight[cacheKey];
        }
        return resolveOverNetwork(property);
    });
}
function resolveOverNetwork(property) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!property.type) {
            throw new Error('Tried to resolve Dynamic Data but the data passed in was not a Dynamic Data property. It was: ' +
                JSON.stringify(property));
        }
        const cacheKey = getCacheKey(property, yield languageInit);
        const payload = Object.assign({}, property);
        delete payload.preloaded;
        delete payload.resolveMode;
        delete payload.preloadBehavior;
        if (!payload.context) {
            delete payload.context;
        }
        if (!(cacheKey in toResolve)) {
            toResolve[cacheKey] = payload;
            requestsInFlight[cacheKey] = createOpenPromise();
        }
        clearTimeout(timer);
        timer = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            const resolvesToSend = toResolve;
            toResolve = {};
            // The sorting here isn't just because it's fun, it's because we need to ensure a determinitic format
            // for the json in the query string. The reason for that is that we use HTTP2 push/preloading of
            // dynamic data with resolveMode=auto. In order to do that we have to anticipate the exact url that
            // the client will make.
            // The same ordering (and removal of some properties) is done in the backend. If you change this in some
            // way please make sure that you make the same change in the backend.
            const sortedResolves = Object.values(resolvesToSend);
            sortDynamicDataResolves(sortedResolves);
            const url = new URLX('/resolvedynamicdata');
            url.searchParams.append('d', generateDynamicDataQueryString(sortedResolves));
            // We don't include the default headers here (such as X-Requested-With since that triggers a
            // bug in Chrome where a preloaded response isn't used)
            try {
                const results = yield getJson(url, {}, false);
                Object.keys(resolvesToSend).forEach((sentCacheKey) => __awaiter(this, void 0, void 0, function* () {
                    const sentResolve = resolvesToSend[sentCacheKey];
                    const index = sortedResolves.indexOf(sentResolve);
                    yield addToCache(sentCacheKey, results[index]);
                    if (sentCacheKey in requestsInFlight) {
                        requestsInFlight[sentCacheKey].resolve(undefined);
                        delete requestsInFlight[sentCacheKey];
                    }
                }));
            }
            catch (e) {
                Object.keys(resolvesToSend).forEach(sentCacheKey => {
                    if (sentCacheKey in requestsInFlight) {
                        requestsInFlight[sentCacheKey].reject(e);
                        delete requestsInFlight[sentCacheKey];
                    }
                });
            }
        }), batchWaitMS);
        yield requestsInFlight[cacheKey];
        return (yield cache.getFastCacheItem(cacheKey));
    });
}
export function sortDynamicDataResolves(resolves) {
    resolves.sort((a, b) => {
        if (a.type < b.type) {
            return -1;
        }
        if (a.type > b.type) {
            return 1;
        }
        return 0;
    });
}
export function generateDynamicDataQueryString(sortedResolves) {
    let lastType;
    const compressedResolvesJsons = sortedResolves.map(r => {
        let type = r.type;
        if (lastType !== type) {
            lastType = type;
        }
        else {
            type = undefined;
        }
        const props = [];
        if (type) {
            props.push('t:"' + type + '"');
        }
        if (r.context) {
            props.push('c:' + JSON.stringify(r.context));
        }
        const json = '{' + props.join(',') + '}';
        return json;
    });
    const allCompressedResolvesJson = '[' + compressedResolvesJsons.join(',') + ']';
    return allCompressedResolvesJson;
}
// You can use the preferCacheOverNetwork flag to try to fetch it from cache instead of network. This is useful
// in a secondary tab in the app, where we'd populate the cache from the primary tab and then resolve from cache
export function autoResolveDynamicData(json, promises, preferCacheOverNetwork = false) {
    let returnPromiseAll = false;
    if (!promises) {
        promises = [];
        returnPromiseAll = true;
    }
    if (!json) {
        return;
    }
    else if (Array.isArray(json)) {
        json.forEach(j => autoResolveDynamicData(j, promises, preferCacheOverNetwork));
    }
    else if (isDynamicData(json)) {
        if (json.preloaded) {
            updateData(json, json.preloaded, json.preloadBehavior);
        }
        else if (json.resolveMode === 'auto') {
            if (preferCacheOverNetwork) {
                promises.push(resolveData(json));
            }
            else {
                promises.push(resolveOverNetwork(json));
            }
        }
    }
    else if (typeof json === 'object') {
        Object.keys(json).forEach(k => autoResolveDynamicData(json[k], promises, preferCacheOverNetwork));
    }
    if (returnPromiseAll) {
        return Promise.all(promises);
    }
    else {
        return Promise.resolve(null);
    }
}
function isDynamicData(obj) {
    const dynamicData = obj;
    return !!dynamicData.type && !!dynamicData.resolveMode;
}
function getCacheKey(property, keyPrefix) {
    const cacheContext = {};
    if (property.context) {
        const keys = Object.keys(property.context);
        keys.sort();
        keys.forEach(key => {
            cacheContext[key] = property.context[key];
        });
    }
    return keyPrefix + property.type + JSON.stringify(cacheContext);
}
export function onDataUpdate(property, callback) {
    if (!__BROWSER__) {
        return () => null;
    }
    languageInit.then(language => {
        const cacheKey = getCacheKey(property, language);
        if (!dataWatchers[cacheKey]) {
            dataWatchers[cacheKey] = [];
        }
        dataWatchers[cacheKey].push(callback);
    });
    return () => {
        languageInit.then(language => {
            const cacheKey = getCacheKey(property, language);
            const idx = dataWatchers[cacheKey].indexOf(callback);
            dataWatchers[cacheKey].splice(idx, 1);
        });
    };
}
let timeout;
let cacheEventQueue = [];
function debounceCacheUpdates(cacheKey, data) {
    cacheEventQueue.push({ cacheKey, data });
    if (timeout) {
        clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
        timeout = null;
        // Only take the last update for each cacheKey
        const cacheKeyToData = {};
        cacheEventQueue.forEach(event => {
            cacheKeyToData[event.cacheKey] = event.data;
        });
        const cacheKeys = Object.keys(cacheKeyToData);
        cacheKeys.forEach(cacheKey => {
            addToCache(cacheKey, cacheKeyToData[cacheKey], true);
        });
        cacheEventQueue = [];
    }, 100);
}
onCrossWindow('dynamic-data-cache-update', (_, data) => {
    debounceCacheUpdates(data.cacheKey, data.data);
});
export function refreshWatchedDataFromSlowCache() {
    return __awaiter(this, void 0, void 0, function* () {
        const fastCacheCopy = Object.assign({}, cache.getFastCache());
        let processing = Object.keys(dataWatchers).map((cacheKey) => __awaiter(this, void 0, void 0, function* () {
            yield cache.clearCacheItem(cacheKey, true);
            let item = yield cache.getCacheItem(cacheKey);
            if (item) {
                yield cache.setCacheItem(item, true);
                if (JSON.stringify(item) !== JSON.stringify(fastCacheCopy[cacheKey]) && dataWatchers[cacheKey]) {
                    dataWatchers[cacheKey].forEach(l => l());
                }
            }
        }));
        yield Promise.all(processing);
    });
}
function addToCache(cacheKey, data, skipCacheBroadcast = false) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!skipCacheBroadcast) {
            emitCrossWindowWithData({
                name: 'dynamic-data-cache-update',
                data: {
                    cacheKey: cacheKey,
                    data: data,
                },
                runImmediately: false,
            });
        }
        data = Object.assign(Object.assign({}, data), { $cacheKey: cacheKey });
        // We await languageInit here because otherwise we might set a cache item that gets thrown
        // out because of language mismatch. So what might happen is:
        // 1) I visist /en and get DD caches for that
        // 2) I go to /de and request DD items in German
        // 3) We get the German DD items, and place them in the cache
        // 4) The cache is now mixed with English and German entries
        // 5) languageInit will nuke the cache and remove the (valid) German entries
        // 6) We'd need to re-request the German entries
        yield languageInit;
        try {
            yield cache.setCacheItem(data, false);
        }
        catch (e) {
            console.error('Could not add to cache, cache key: ' + cacheKey, data);
        }
        callDataWatchers(cacheKey);
    });
}
function callDataWatchers(cacheKey) {
    if (dataWatchers[cacheKey]) {
        dataWatchers[cacheKey].forEach(dw => dw());
    }
}
